/**
 * Global client-side error capture
 *
 * Installs window-level handlers for uncaught errors and unhandled promise
 * rejections, buffering them in the telemetry store so they flush to the
 * backend on the next session heartbeat (see usePresenceTracker /
 * webapp/routes/sessions.py). React component crashes are captured separately
 * by ErrorBoundary / ChunkLoadErrorBoundary via recordError('react'|'chunk').
 *
 * Also auto-recovers from the Firestore SDK's IndexedDB corruption guard:
 * when site data is cleared while a tab is open (DevTools "Clear site data",
 * cleaner utilities, browser storage eviction), the SDK permanently refuses
 * to reopen its IndexedDB database for the lifetime of the tab and every
 * Firestore operation fails in a retry loop. The SDK's own remedy is a page
 * reload (its in-memory lastClosedDbVersion guard resets), so we trigger one
 * automatically — guarded by a sessionStorage cooldown to prevent a reload
 * loop if reloading doesn't clear the condition.
 *
 * Idempotent — safe to call once at startup (main.tsx).
 */

import { useTelemetryStore } from '../store/telemetryStore';

let installed = false;
let errorHandler: ((event: ErrorEvent) => void) | null = null;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

// --- Firestore IndexedDB corruption auto-recovery ---------------------------

/** Distinctive substring of the @firebase/firestore SimpleDb corruption error. */
const IDB_CORRUPTION_SNIPPET = 'refusing to open IndexedDB database';

/** sessionStorage key holding JSON: { ts: epoch ms of last auto-reload, noted: boolean } */
const IDB_RELOAD_STATE_KEY = 'botr-idb-corruption-reload';

/** Minimum gap between auto-reloads — prevents a reload loop if a reload doesn't fix it. */
export const IDB_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** Indirection so tests can stub the reload (jsdom can't navigate). */
let reloadFn: () => void = () => window.location.reload();

interface IdbReloadState {
  ts: number;
  noted: boolean;
}

function readReloadState(): IdbReloadState | null {
  try {
    const raw = sessionStorage.getItem(IDB_RELOAD_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IdbReloadState>;
    if (typeof parsed.ts !== 'number') return null;
    return { ts: parsed.ts, noted: parsed.noted === true };
  } catch {
    return null;
  }
}

function writeReloadState(state: IdbReloadState): boolean {
  try {
    sessionStorage.setItem(IDB_RELOAD_STATE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

/**
 * If `message` is the Firestore IndexedDB corruption error, reload the page
 * once (per cooldown window) to reset the SDK's in-memory guard. Without this
 * the tab is permanently broken — every Firestore operation fails until the
 * user reloads manually.
 */
function maybeRecoverFromIdbCorruption(message: string): void {
  if (!message.includes(IDB_CORRUPTION_SNIPPET)) return;

  const prior = readReloadState();
  if (prior && Date.now() - prior.ts < IDB_RELOAD_COOLDOWN_MS) return;

  // If sessionStorage is unusable we can't guard against a reload loop — bail.
  if (!writeReloadState({ ts: Date.now(), noted: false })) return;

  reloadFn();
}

/**
 * After an auto-reload, record a single telemetry entry documenting the
 * recovery. The pre-reload error buffer is lost with the page, so without
 * this the incident would be invisible in Session History.
 */
function noteRecoveryAfterReload(): void {
  const state = readReloadState();
  if (!state || state.noted) return;
  writeReloadState({ ...state, noted: true });
  useTelemetryStore.getState().recordError(
    'error',
    '[auto-recovery] Page reloaded after Firestore IndexedDB corruption error (site data was cleared while the tab was open)',
    undefined,
    window.location?.pathname
  );
}

// --- Handler installation ---------------------------------------------------

/** Extract a message + stack from an arbitrary thrown/rejected value. */
function describe(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return { message: value.message || value.name || 'Error', stack: value.stack };
  }
  if (typeof value === 'string') {
    return { message: value };
  }
  if (value && typeof value === 'object') {
    const maybe = value as { message?: unknown; stack?: unknown };
    if (typeof maybe.message === 'string') {
      return {
        message: maybe.message,
        stack: typeof maybe.stack === 'string' ? maybe.stack : undefined,
      };
    }
  }
  return { message: String(value) };
}

export function installGlobalErrorCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  noteRecoveryAfterReload();

  errorHandler = (event: ErrorEvent) => {
    const { message, stack } = describe(event.error ?? event.message);
    useTelemetryStore.getState().recordError('error', message, stack, window.location?.pathname);
    maybeRecoverFromIdbCorruption(message);
  };
  rejectionHandler = (event: PromiseRejectionEvent) => {
    const { message, stack } = describe(event.reason);
    useTelemetryStore.getState().recordError('rejection', message, stack, window.location?.pathname);
    maybeRecoverFromIdbCorruption(message);
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
}

/** Test-only: remove the handlers and reset the install guard. */
export function __resetGlobalErrorCaptureForTests(): void {
  if (typeof window !== 'undefined') {
    if (errorHandler) window.removeEventListener('error', errorHandler);
    if (rejectionHandler) window.removeEventListener('unhandledrejection', rejectionHandler);
    try {
      sessionStorage.removeItem(IDB_RELOAD_STATE_KEY);
    } catch {
      // storage unavailable — nothing to clean
    }
  }
  errorHandler = null;
  rejectionHandler = null;
  installed = false;
  reloadFn = () => window.location.reload();
}

/** Test-only: stub the page reload (jsdom cannot navigate). */
export function __setReloadFnForTests(fn: () => void): void {
  reloadFn = fn;
}
